import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { LoggerService } from '../../../../services/logger/logger.service';
import { NotificationService } from '../../../general/services/notification.service';
import { ScanPartnerCodeInfo } from '../../../partner-code/models/scan-partner-code-info';
import { SmartCardCommunicationService } from '../../../smart-card/services/smart-card-communication.service';
import { TouchTileService } from '../../../vu/services/touch-tile.service';
import { RefundStepEnum } from '../../models/refund-step-enum';
import { RefundCardStateService } from '../../services/refund-card-state.service';

@Component({
  selector: 'app-card-refund',
  templateUrl: './card-refund.component.html',
  styleUrls: ['./card-refund.component.css']
})
export class CardRefundComponent implements OnInit, OnDestroy {

  private subscription: Subscription;
  private ledSubscription: Subscription;

  constructor(
    private router: Router,
    private refundCardStateService: RefundCardStateService,
    private loggerService: LoggerService,
    private notificationService: NotificationService,
    private touchTileService: TouchTileService,
    private smartCardCommunicationService: SmartCardCommunicationService,
  ) {
  }

  ngOnInit() {
    this.ledSubscription = this.smartCardCommunicationService.ledEnabled.subscribe(state => {
      this.touchTileService.cardDispenserEnable(state);
    });
    this.subscription = this.refundCardStateService.reset().subscribe(
      result => {
        if (!result) {
          this.navigateToMainPage();
        }
      },
      error => {
        const errorMessage = error && error.message ? error.message : error;
        this.loggerService.error(errorMessage);
        this.notificationService.show(CardRefundComponent.name, errorMessage);
      }
    );
  }

  ngOnDestroy() {
    this.notificationService.hide(CardRefundComponent.name);
    this.touchTileService.cardDispenserEnable(false);
    this.unsubscribe();
    this.ledUnsubscribe();
  }

  unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  ledUnsubscribe(): void {
    if (this.ledSubscription) {
      this.ledSubscription.unsubscribe();
      this.ledSubscription = null;
    }
  }

  onNext(): void {
    this.unsubscribe();
    this.subscription = this.refundCardStateService.nextStep().subscribe(
      _ => {
      },
      error => {
        const errorMessage = error && error.message ? error.message : error;
        this.loggerService.error(errorMessage);
        this.notificationService.show(CardRefundComponent.name, errorMessage);
      }
    )
  }

  onBack(): void {
    this.unsubscribe();
    this.subscription = this.refundCardStateService.prevStep().subscribe(
      stepExist => {
        if (!stepExist) {
          this.navigateToMainPage();
        }
      },
      error => {
        const errorMessage = error && error.message ? error.message : error;
        this.loggerService.error(errorMessage);
        this.notificationService.show(CardRefundComponent.name, errorMessage);
      }
    )
  }

  navigateToMainPage(): void {
    this.router.navigateByUrl('');
  }

  get showPleaseWaitMessage(): boolean {
    return this.refundCardStateService.state === RefundStepEnum.None ||
      this.refundCardStateService.state === RefundStepEnum.Refund ||
      !this.isAvailableAmount;
  }

  get showTakeCardMessage(): boolean {
    return this.refundCardStateService.state === RefundStepEnum.ReturnCard;
  }

  get showInsertCardMessage(): boolean {
    return this.refundCardStateService.state === RefundStepEnum.InsertCard;
  }

  get showBalanceMessage(): boolean {
    return this.refundCardStateService.state === RefundStepEnum.Balance;
  }

  get showLoginForm(): boolean {
    return this.refundCardStateService.state === RefundStepEnum.Login;
  }

  get hideNext(): boolean {
    return this.refundCardStateService.state !== RefundStepEnum.Balance ||
      !this.isAvailableAmount;
  }

  get cardBalance(): string {
    return this.toMoneyText(this.refundCardStateService.cardBalance);
  }

  get guestCardPrice(): string {
    return this.toMoneyText(this.refundCardStateService.guestCardPrice);
  }

  get availableAmount(): string {
    return this.toMoneyText(this.refundCardStateService.availableAmount);
  }

  toMoneyText(value: number): string {
    return environment.globalSettings.toMoneyStr(value);
  }

  get scanPartnerCodeInfo(): ScanPartnerCodeInfo {
    return this.refundCardStateService.scanPartnerCodeInfo;
  }

  get messageBalance(): string {
    return 'You Balance';
  }

  get messageAvailableAmount(): string {
    return 'Available Amount';
  }

  get isAvailableAmount(): boolean {
    return this.refundCardStateService.availableAmount !== -1;
  }

  get isCardBalance(): boolean {
    return this.refundCardStateService.cardBalance !== -1 &&
      this.refundCardStateService.guestCardPrice !== -1;
  }

}
